import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import { useRealtime } from "../../hooks/useRealtime";
import {
  Hand, MessageSquare, Mic, SkipForward, X, History,
  ShieldCheck, AlertCircle, Check, Clock, Users,
} from "lucide-react";

/**
 * Module 2 — Chair's Hybrid Dashboard.
 *
 *   Left  : Live placard queue (FIFO) with Recognise / Skip / Lower
 *   Right : Pending chit moderation queue with Approve / Reject (+ reason)
 *   Below : Recent chit audit trail
 *
 * Pure WebSocket sync via `useRealtime` — both columns update the moment
 * a delegate raises a placard or sends a chit anywhere in the room.
 *
 * Designed for desktop / tablet (chairs sit at a podium with a laptop)
 * but the layout still gracefully collapses to mobile.
 */
export default function ChairHybridDashboard() {
  const { eventId } = useParams();
  const [queue, setQueue] = useState([]);
  const [chitQueue, setChitQueue] = useState([]);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState({});  // id → true while action in flight

  const loadAll = () => {
    api.get(`/committee/placards/queue?event_id=${eventId}`)
      .then((r) => setQueue(r.data.queue || [])).catch(() => {});
    api.get(`/committee/chits/queue?event_id=${eventId}`)
      .then((r) => setChitQueue(r.data.queue || [])).catch(() => {});
    api.get(`/committee/chits/audit?event_id=${eventId}`)
      .then((r) => setAudit(r.data.items || [])).catch(() => {});
  };
  useEffect(loadAll, [eventId]);

  useRealtime(
    [`placards:${eventId}`, `chits:${eventId}`],
    () => loadAll(),
  );

  const placardAction = async (id, action) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post("/committee/placards/action", { placard_id: id, action });
      loadAll();
    } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };
  const moderate = async (id, action, reason) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post("/committee/chits/moderate",
        { chit_id: id, action, reason });
      loadAll();
    } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="chair-hybrid-root">
        <header className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] font-bold flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> Chair's Hybrid Dashboard
          </p>
          <h1 className="mt-1 text-2xl font-bold text-[#0A192F]">
            Floor control
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Real-time placard queue and chit moderation. Every action pushes
            instantly to delegate screens — in-room and virtual.
          </p>
        </header>

        <div className="grid lg:grid-cols-3 gap-6">
          <PlacardQueueCard queue={queue} busy={busy} onAction={placardAction} />
          <ChitModerationCard queue={chitQueue} busy={busy} onModerate={moderate} />
        </div>

        <AuditCard items={audit} />
      </div>
    </DashboardLayout>
  );
}

// ---------------------------------------------------------------------------
// Placard queue
// ---------------------------------------------------------------------------
function PlacardQueueCard({ queue, busy, onAction }) {
  const recognised = queue.find((q) => q.status === "recognised");
  const waiting = queue.filter((q) => q.status === "raised");
  return (
    <section className="lg:col-span-2 rounded-2xl border-2 border-[#D4AF37] bg-white p-5"
             data-testid="placard-queue-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
          <Hand className="h-4 w-4 text-[#D4AF37]" /> Placard queue
        </h2>
        <span className="text-xs text-slate-500 flex items-center gap-1">
          <Users className="h-3 w-3" /> {queue.length} active
        </span>
      </div>

      {recognised && (
        <div className="mb-4 rounded-xl border-2 border-emerald-500 bg-emerald-50 p-4"
             data-testid="placard-now-speaking">
          <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-bold flex items-center gap-1">
            <Mic className="h-3 w-3" /> Now speaking
          </p>
          <p className="mt-1 text-lg font-bold text-[#0A192F]">
            {recognised.delegate_country || recognised.delegate_name}
          </p>
          <p className="text-xs text-slate-600 mt-0.5">{recognised.purpose}</p>
          <button
            type="button"
            onClick={() => onAction(recognised.id, "complete")}
            disabled={busy[recognised.id]}
            className="mt-3 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
            data-testid="placard-complete-btn"
          >
            <Check className="h-3 w-3" /> Mark complete
          </button>
        </div>
      )}

      {waiting.length === 0 && !recognised && (
        <p className="py-8 text-center text-sm text-slate-400">
          No placards raised. The queue will update in real time.
        </p>
      )}

      <ol className="space-y-2">
        {waiting.map((q, i) => (
          <li key={q.id}
              className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50"
              data-testid={`placard-row-${i}`}>
            <span className="font-mono font-bold text-slate-500 w-7">#{i + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0A192F] truncate">
                {q.delegate_country || q.delegate_name}
              </p>
              <p className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                <Clock className="h-3 w-3" /> {timeShort(q.raised_at)} · {q.purpose}
              </p>
            </div>
            <div className="flex gap-1">
              <button
                type="button" onClick={() => onAction(q.id, "recognise")}
                disabled={busy[q.id] || !!recognised}
                title={recognised ? "Complete the current speaker first" : "Recognise"}
                className="p-1.5 rounded-md bg-[#0F4C81] text-white disabled:opacity-40"
                data-testid={`placard-recognise-${i}`}
              >
                <Mic className="h-3.5 w-3.5" />
              </button>
              <button
                type="button" onClick={() => onAction(q.id, "skip")}
                disabled={busy[q.id]}
                className="p-1.5 rounded-md bg-amber-500 text-white disabled:opacity-40"
                title="Skip" data-testid={`placard-skip-${i}`}
              >
                <SkipForward className="h-3.5 w-3.5" />
              </button>
              <button
                type="button" onClick={() => onAction(q.id, "lower")}
                disabled={busy[q.id]}
                className="p-1.5 rounded-md bg-slate-500 text-white disabled:opacity-40"
                title="Lower" data-testid={`placard-lower-${i}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Chit moderation
// ---------------------------------------------------------------------------
function ChitModerationCard({ queue, busy, onModerate }) {
  const [reasonFor, setReasonFor] = useState({}); // chit_id → reason text
  return (
    <section className="rounded-2xl border-2 border-[#0F4C81] bg-white p-5"
             data-testid="chit-moderation-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-[#0F4C81]" /> Pending chits
        </h2>
        <span className="text-xs text-slate-500">{queue.length} waiting</span>
      </div>

      {queue.length === 0 && (
        <p className="py-8 text-center text-sm text-slate-400">
          No chits awaiting moderation.
        </p>
      )}

      <ul className="space-y-3 max-h-[600px] overflow-y-auto">
        {queue.map((c) => (
          <li key={c.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3"
              data-testid={`chit-row-${c.id}`}>
            <p className="text-[11px] text-slate-500">
              <strong className="text-[#0A192F]">{c.sender_name}</strong> →{" "}
              <strong className="text-[#0A192F]">{c.recipient_name}</strong>{" "}
              · {timeShort(c.sent_at)}
            </p>
            <p className="mt-1.5 text-sm text-[#0A192F]">{c.body}</p>

            <input
              type="text"
              placeholder="Optional reason if rejecting…"
              value={reasonFor[c.id] || ""}
              onChange={(e) => setReasonFor((r) => ({ ...r, [c.id]: e.target.value }))}
              className="mt-2 w-full rounded-md border border-slate-300 px-2 py-1 text-xs"
              data-testid={`chit-reason-${c.id}`}
              maxLength={200}
            />
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => onModerate(c.id, "approve")}
                disabled={busy[c.id]}
                className="flex-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
                data-testid={`chit-approve-${c.id}`}
              >
                <Check className="h-3 w-3" /> Approve
              </button>
              <button
                type="button"
                onClick={() => onModerate(c.id, "reject", reasonFor[c.id])}
                disabled={busy[c.id]}
                className="flex-1 px-3 py-1.5 rounded-md bg-rose-600 text-white text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
                data-testid={`chit-reject-${c.id}`}
              >
                <X className="h-3 w-3" /> Reject
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Audit trail
// ---------------------------------------------------------------------------
function AuditCard({ items }) {
  return (
    <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5"
             data-testid="chit-audit-card">
      <h2 className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
        <History className="h-4 w-4" /> Recent moderation ({items.length})
      </h2>
      {items.length === 0 && (
        <p className="mt-3 text-sm text-slate-400">No moderation actions yet.</p>
      )}
      <ul className="mt-3 divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
        {items.slice(0, 50).map((a) => (
          <li key={a.id} className="py-2 flex items-center gap-2 text-xs"
              data-testid={`audit-row-${a.id}`}>
            <span className={`uppercase tracking-wider font-bold text-[10px] px-1.5 py-0.5 rounded ${
              a.action === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
            }`}>
              {a.action}
            </span>
            <span className="text-[#0A192F] font-medium">{a.by_name}</span>
            <span className="text-slate-400 truncate flex-1">{a.reason || ""}</span>
            <span className="text-slate-400">{timeShort(a.at)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function timeShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
